/* eslint-disable @typescript-eslint/no-explicit-any */

import classNames from "classnames";
import { NavItem } from "./defaultNavItems";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  AlignJustify,
  AlignLeft,
  AlignRight,
  ChevronFirst,
  ChevronLast,
  Cog,
  LogOut,
  Menu,
  Settings,
  User,
  UserCircleIcon,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ToggleTheme } from "@/components/toggle-theme";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import AppBreadcrumbs from "../app-breadcrumb";
import { Button } from "@/components/ui/button";
import Logo from "@/shared/components/Logo";

interface Props {
  routes: NavItem[];
  onMenuButtonClick(): void;
  expanded: boolean;
  dispatch: React.Dispatch<string>;
  appUser: any;
  open: boolean;
}
const Navbar = (props: Props) => {
  const { dispatch, expanded, open } = props;
  const { user, logOut } = useAuth();

  const location = useLocation();
  const matchingPath = props.routes.find(
    (item) => item.href === location.pathname
  );

  return (
    <nav
      className={classNames({
        "border-b": true,
        "flex items-center": true,
        "md:w-full z-[50] px-4 h-[73px] max-h-[73px] top-0 ": true,
      })}
    >
      <button
        onClick={() => dispatch("TOGGLE")}
        className="p-1.5 hidden sm:block rounded-lg hover:bg-secondary-200 hover:text-secondary-900 mr-4"
      >
        {expanded ? <AlignRight /> : <AlignLeft />}
      </button>
      <button
        className="sm:hidden rounded-lg hover:bg-secondary-200 hover:text-secondary-900 mr-4"
        onClick={() => dispatch("TOGGLE_MOBILE")}
      >
        {!open && <AlignRight className="h-8 w-8 " />}
      </button>
      {!open && (
        <div className="sm:hidden mr-4">
          <Logo expanded={false} />
        </div>
      )}
      {/* <div className="font-bold text-lg">{matchingPath?.label}</div> */}
      <AppBreadcrumbs />
      <div className="flex-grow"></div>
      {
        <div className="sm:ml-6 sm:flex sm:items-center w-fit">
          <ToggleTheme />
          <div className="hidden sm:block">
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                className="flex rounded text-sm focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 no-mouse-focus-ring"
              >
                <Button
                  variant="ghost"
                  className="relative h-10 w-10 rounded-full"
                >
                  <div className="flex items-center justify-center">
                    <span className="sr-only">Open user menu</span>
                    {user?.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt="Profile"
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="size-5" />
                    )}
                    {/* <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 ring-1 ring-background"></span> */}
                  </div>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent className="w-64 p-2" align="end">
                <div className="flex flex-col items-center gap-2 p-2">
                  <div className="h-16 w-16 rounded-full overflow-hidden">
                    {user?.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-secondary-100">
                        <UserCircleIcon className="h-10 w-10 text-secondary-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="font-medium">
                      {/* {user?.name || "User Name"} */}
                      {user?.email || "user@example.com"}
                    </span>
                    <span className="text-xs text-muted-foreground capitalize">
                      {user?.role}
                    </span>
                  </div>
                </div>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                  {/* <DropdownMenuItem className="gap-2 cursor-pointer">
                    <User className="h-4 w-4" />
                    <span>Profile</span>
                  </DropdownMenuItem> */}
                  <DropdownMenuItem className="gap-2 cursor-pointer" asChild>
                    <Link to="/app/settings">
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem className="gap-2 cursor-pointer">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <line x1="10" y1="9" x2="8" y2="9"></line>
                    </svg>
                    <span>Activity Log</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2 cursor-pointer">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    </svg>
                    <span>Security</span>
                  </DropdownMenuItem> */}
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={logOut}
                  className="gap-2 cursor-pointer text-red-500 focus:text-red-500"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      }
    </nav>
  );
};

export { Navbar };
